import { useMemo } from 'react';
import { HtmlVector } from 'record-it-utils/misc/HtmlVector/HtmlVector';
import { StringUtils } from 'record-it-utils/utils/StringUtils/StringUtils';
import { ElementSize } from '../../hooks/useElementSize/useElementSize';

export function useCalculatedTickConfiguration({
  containerSize,
  tickConfiguration
}: UseCalculatedTickConfigurationOptions): {
  outerTicks: Array<CalculatedTick>;
  innerTicks: Array<CalculatedTick>;
  allTicks: Array<CalculatedTick>;
} {
  const outerTicks = useCalculatedTicks({
    containerSize,
    ticks: tickConfiguration.outerTicks,
    radiusFactor: 0.8
  });

  const innerTicks = useCalculatedTicks({
    containerSize,
    ticks: tickConfiguration.innerTicks,
    radiusFactor: 0.54
  });

  return useMemo(
    () => ({
      outerTicks,
      innerTicks,
      allTicks: outerTicks.concat(innerTicks)
    }),
    [outerTicks, innerTicks]
  );
}

export type UseCalculatedTickConfigurationOptions = {
  containerSize: ElementSize | null;
  tickConfiguration: TickConfiguration;
};

export type TickConfiguration = {
  outerTicks: Array<number>;
  innerTicks: Array<number>;
};

export type CalculatedTick = {
  value: number;
  displayedValue: string;
  position: HtmlVector;
  selectable: boolean;
};

function useCalculatedTicks({
  containerSize,
  ticks,
  radiusFactor
}: UseCalculatedTicksOptions): Array<CalculatedTick> {
  return useMemo(() => {
    if (!containerSize) {
      return [];
    }

    const minDimension = Math.min(
      containerSize.clientWidth,
      containerSize.clientHeight
    );
    const radius = (minDimension / 2) * radiusFactor;
    const centerVector = new HtmlVector(
      containerSize.clientWidth / 2,
      containerSize.clientHeight / 2
    );

    return generateTicks(ticks, (value, angle) => {
      const position = new HtmlVector(0, radius * -1)
        .rotate(angle * -1)
        .addVector(centerVector);

      return {
        value,
        displayedValue: StringUtils.padLeft(value.toString(), 2),
        position
      };
    });
  }, [containerSize, ticks, radiusFactor]);
}

/**
 * Also generates "filler" ticks for all integer numbers, so even values which are not configured, can be displayed. These fillers are marked as non selectable and shouldn't be rendered.
 */
function generateTicks(
  configuredTicks: Array<number>,
  calculateTick: (
    value: number,
    angle: number
  ) => Omit<CalculatedTick, 'selectable'>
): Array<CalculatedTick> {
  const firstConfiguredTick = configuredTicks[0];
  const lastConfiguredTick = configuredTicks[configuredTicks.length - 1];

  if (firstConfiguredTick === undefined || lastConfiguredTick === undefined) {
    // must be an empty array
    return [];
  }

  const projectedMaxValue =
    firstConfiguredTick +
    ((lastConfiguredTick - firstConfiguredTick) * configuredTicks.length) /
      (configuredTicks.length - 1);
  const anglePerCount = 360 / (projectedMaxValue - firstConfiguredTick);

  const calculatedTicks: Array<CalculatedTick> = [];

  for (let i = firstConfiguredTick; i < projectedMaxValue; i++) {
    calculatedTicks.push({
      ...calculateTick(i, anglePerCount * (i - firstConfiguredTick)),
      selectable: configuredTicks.includes(i)
    });
  }

  return calculatedTicks;
}

type UseCalculatedTicksOptions = {
  containerSize: ElementSize | null;
  ticks: Array<number>;
  radiusFactor: number;
};
